import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { QuoteForm } from '../../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { Utils } from '../../../../common/js/Utils';
import { DEFAULT_AGE, VSTChartLabelValues, VSTClientInfo, VSTLabelEvent, VSTResult } from '../../interfaces/VST';
import { VSTService } from '../../services/vst.service';
import { VstChartComponent } from './vst-chart/vst-chart.component';
import { SafeInviteInfo } from '../../../../common/js/customer/SafeInviteInfo';
import { SegmentService } from '../../../angular/services/analytics/segment.service';

@Component({
  selector: 'visual-sales-tool',
  moduleId: module.id,
  templateUrl: './vst.component.html',
})
export class VstComponent implements OnInit {
  @ViewChild('vstchart') vstchart: VstChartComponent;
  @Input() template: string;
  @Input() quote$: Observable<QuoteForm>;
  vstClientInfo: VSTClientInfo;
  vstData: VSTResult | null;
  age: number;
  private minAge: number;
  labels: Array<string> = ['totalCashValues', 'totalDeathBenefits'];
  optionalLabels: Array<string> = ['guaranteedCashValues', 'guaranteedDeathBenefits', 'accumulatedPaidPremium'];
  labelValues: VSTChartLabelValues;
  @Input() showRidersDisclaimer: boolean = false;
  @Input() safeInviteInfo: SafeInviteInfo = null;
  @Output() mecYear = new EventEmitter<number>();
  @Output() dataRendered = new EventEmitter<boolean>();

  constructor(
    private vstService: VSTService,
    private cdr: ChangeDetectorRef,
    private segmentService: SegmentService,
    private ref: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.labelValues = {
      totalDeathBenefits: '',
      totalCashValues: '',
      guaranteedDeathBenefits: '',
      guaranteedCashValues: '',
      accumulatedPaidPremium: '',
    };
    this.age = DEFAULT_AGE;
    if (this.quote$) {
      this.quote$.subscribe(async (quote) => {
        this.age = DEFAULT_AGE;
        this.vstClientInfo = this.vstService.getVSTClientData(quote, this.safeInviteInfo);
        await this.updateChart(quote);
        this.vstchart.updateAge(this.age);
      });
    } else {
      this.vstClientInfo = this.vstService.getVSTClientData(null, this.safeInviteInfo);
      await this.updateChart();
    }
  }

  onAgeChange(age: number): void {
    this.age = age;
    this.updateData();
    CoverpathUtils.dataLayerPush({
      event: 'vst-interaction',
      eventAction: 'Interact with Chart',
      eventLabel: 'Update Age',
      eventValue: this.age,
    });

    this.segmentService.handleVstAgeChangedEvent(this.age);
  }

  async updateChart(quote?: QuoteForm) {
    // @TODO HL-20924 - Add a loading indicator
    this.vstData = await this.vstService.getVSTData(quote, this.safeInviteInfo);
    await this.loadData();
    this.cdr.markForCheck();
    // tell parent component that the data on VST is available
    this.dataRendered.emit(true);
  }

  async loadData() {
    this.age = DEFAULT_AGE;
    if (this.vstData) {
      this.minAge = this.vstData.ages[0];
      this.mecYear.emit(this.vstData.mecYear);
      this.renderChart();
    }
  }

  renderChart(): void {
    this.updateData();
    const vstData = Object.assign({}, this.vstData);
    delete vstData.ages;
    delete vstData.currentPaidPremiums;
    this.vstchart.buildSeries(vstData, this.minAge);
    this.vstchart.renderChart(vstData);
  }

  updateData(): void {
    const ageIndex = this.age - this.minAge;
    this.labelValues.totalDeathBenefits = Utils.formatCurrency(this.vstData.totalDeathBenefits.data[ageIndex], 0);
    this.labelValues.totalCashValues = Utils.formatCurrency(this.vstData.totalCashValues.data[ageIndex], 0);
    this.labelValues.guaranteedDeathBenefits = Utils.formatCurrency(
      this.vstData.guaranteedDeathBenefits.data[ageIndex],
      0
    );
    this.labelValues.guaranteedCashValues = Utils.formatCurrency(this.vstData.guaranteedCashValues.data[ageIndex], 0);
    this.labelValues.accumulatedPaidPremium = Utils.formatCurrency(
      this.vstData.accumulatedPaidPremium.data[ageIndex],
      0
    );
  }

  updateLabels($event: VSTLabelEvent): void {
    this.vstchart.displaySeries($event.displayState, $event.series);
    const eventLabel = `${$event.displayState ? 'Show' : 'Hide'} ${$event.label}`;
    CoverpathUtils.dataLayerPush({
      event: 'vst-interaction',
      eventAction: 'Interact with Legend',
      eventLabel: eventLabel,
    });

    // $event.series could be: guaranteedCashValues || guaranteedDeathBenefits || accumulatedPaidPremium
    // $event.displayState could be: true || false
    this.segmentService.handleVstDisplayStateChangedEvent($event.series, $event.displayState ? 'checked' : 'uncheck');
  }
}
